'use client'

import { Avatar, Button } from '@chain-zen/ui'
import { useState } from 'react'
import { toast } from 'sonner'

import { AvatarsGrid } from '../profile-picture/avatars-grid'
import { ProfilePictureUploader } from '../profile-picture/profile-picture-uploader'

export interface IImageState {
  file?: Blob
  type: 'avatar' | 'upload'
  url: string
}

export interface IBasePictureEditorProps {
  avatarVariant?: 'square' | 'basic'
  buttonText?: string
  defaultAvatarPath?: string
  defaultAvatarUrl?: string
  entityDisplayName?: string
  entityName?: string
  isLoading: boolean
  isRemovingPicture: boolean
  onAvatarUploadAction: (avatarUrl: string) => Promise<void>
  onFileUploadAction: (file: Blob) => Promise<void>
  onRemovePicture?: () => Promise<void>
  onSuccess?: (url?: string) => void
  removeButtonText?: string
  showAvatarSelection?: boolean
  showRemoveButton?: boolean
  subtitle?: string
  title?: string
  uploadButtonText?: string
}

export const BasePictureEditor = ({
  avatarVariant = 'basic',
  buttonText = 'Save',
  defaultAvatarUrl,
  defaultAvatarPath = 'default-profile-avatars',
  entityName = 'profile',
  entityDisplayName = '',
  isLoading,
  isRemovingPicture,
  onAvatarUploadAction,
  onFileUploadAction,
  onRemovePicture,
  onSuccess,
  removeButtonText = 'Remove Picture',
  showAvatarSelection = false,
  showRemoveButton = false,
  subtitle = 'Upload a photo or choose from our collection of avatars',
  title = 'Set Picture',
  uploadButtonText = 'Upload Picture'
}: IBasePictureEditorProps) => {
  const [alertError, setError] = useState('')
  const [selectedImage, setSelectedImage] = useState<IImageState>(
    defaultAvatarUrl
      ? {
          type: 'avatar',
          url: defaultAvatarUrl
        }
      : {
          type: 'avatar',
          url: entityName === 'profile' ? `https://chaine-images.s3.amazonaws.com/${defaultAvatarPath}/1.png` : ''
        }
  )

  const handleImageUpload = (file: Blob) => {
    setSelectedImage({
      file,
      type: 'upload',
      url: URL.createObjectURL(file)
    })
  }

  const handleAvatarSelect = (avatarUrl: string) => {
    setSelectedImage({
      type: 'avatar',
      url: avatarUrl
    })
  }

  const handleRemovePicture = async () => {
    if (!onRemovePicture) return

    try {
      await onRemovePicture()
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      console.error(`Error removing ${entityName} picture:`, error)
      setError(`Failed to remove ${entityName} picture. Please try again.`)
    }
  }

  const submitPicture = async () => {
    try {
      if (selectedImage.type === 'upload' && selectedImage.file) {
        await onFileUploadAction(selectedImage.file)
      } else {
        await onAvatarUploadAction(selectedImage.url)
      }

      if (onSuccess) {
        onSuccess(selectedImage.url)
      } else {
        toast.success(`${entityName.charAt(0).toUpperCase() + entityName.slice(1)} picture updated successfully`)
      }
    } catch (error) {
      setError(`Failed to update ${entityName} picture. Please try again.`)
    }
  }

  // Determine if the submit button should be disabled
  const isSubmitDisabled = isLoading || !selectedImage.url

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1>{title}</h1>
        <p className="text-gray-600 dark:text-gray-300 mt-2">{subtitle}</p>
      </div>

      <Avatar
        variant={avatarVariant}
        className="flex items-center justify-center mx-auto h-24 sm:h-32 w-24 sm:w-32 opacity-400"
        aria-label={`${entityName}-picture`}
        src={selectedImage.url}
        alt={entityDisplayName}
        name={entityDisplayName}
      />

      <ProfilePictureUploader
        onImageUpload={handleImageUpload}
        alertError={alertError}
        showRemoveButton={!!showRemoveButton && !!defaultAvatarUrl && !!onRemovePicture}
        onRemovePhoto={handleRemovePicture}
        isRemovingPhoto={isRemovingPicture}
        uploadButtonText={uploadButtonText}
        removeButtonText={removeButtonText}
      />

      {!!showAvatarSelection && (
        <div className="text-center space-y-4">
          <h2 className="text-xl font-semibold">Or choose an avatar!</h2>
          <AvatarsGrid onAvatarClickAction={handleAvatarSelect} />
        </div>
      )}

      <Button
        className="w-full"
        size="lg"
        onClick={submitPicture}
        disabled={isSubmitDisabled}
        loading={isLoading}
        data-testid={`submit-${entityName}-picture`}
        variant="default"
      >
        {isLoading ? 'Uploading...' : buttonText}
      </Button>
    </div>
  )
}
